// ============================================
// ScriptComputeRuntime — shared device runtime for user-space compute kernels
// ============================================
//
// The GPU half of `engine.gpu` v2, shared by the EDITOR backing (WGSL from the import cache /
// live compile) and the PLAYER backing (build-emitted precompiled map). Runs on the RAW device
// (own command encoders, outside the frame graph) so script compute never fights the renderer's
// bind-group conventions.
//
// Key decisions (Phase-0 spike, .refactor/COMPUTE-SHADERS-DESIGN.md):
//  - EXPLICIT bind-group layout built from the module's reflection — `layout:'auto'` is unusable
//    (it prunes per-entry-point, so kernels of one module would disagree on the layout);
//  - one layout/shader-module/pipeline-set per MODULE, one uniform buffer + bind group per KERNEL
//    INSTANCE (two scripts loading the same asset must not share uniform values);
//  - resources the current kernel does NOT own (sibling struct-kernel fields) are dummy-filled;
//    the kernel's OWN bindings are required — dispatch refuses (warn-once) while any is missing;
//  - containment: nothing here throws or rejects; failures produce dead handles / null reads and
//    a log line. Device loss flips isAvailable() and turns every op into a no-op.

import type {
  ComputeBindingEntry,
  ComputeKernelReflection,
  ComputeShaderReflection,
} from './types';

/** Compiled artifact a backing feeds the runtime (from the import cache or the build snapshot). */
export interface ComputeModuleArtifact {
  wgsl: string;
  reflection: ComputeShaderReflection;
}

/** What createKernel resolves — structurally satisfies @engine/core's GpuKernelHandle. */
export interface ComputeKernelRuntimeHandle {
  id: number;
  ok: boolean;
  name: string;
  numThreads: [number, number, number];
  resources: ComputeBindingEntry[];
  error?: string;
}

interface ModuleRuntime {
  shaderModule: GPUShaderModule;
  layout: GPUBindGroupLayout;
  pipelineLayout: GPUPipelineLayout;
  reflection: ComputeShaderReflection;
  pipelines: Map<string, GPUComputePipeline>;
}

interface KernelInstance {
  module: ModuleRuntime;
  kernel: ComputeKernelReflection;
  pipeline: GPUComputePipeline;
  /** Per-instance uniform staging (undefined when the module has no uniform block). */
  uniformData?: ArrayBuffer;
  uniformBuffer?: GPUBuffer;
  uniformsDirty: boolean;
  /** name → bound resource id (buffers and textures share the name space per kernel). */
  bound: Map<string, number>;
  bindGroup: GPUBindGroup | null;
  bindVersion: number;
  builtVersion: number;
  warnedMissing: boolean;
  label: string;
}

interface LogLike {
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
}

const sampleTypeOf = (typeLabel: string): GPUTextureSampleType =>
  typeLabel.startsWith('uint') ? 'uint' : typeLabel.startsWith('int') ? 'sint' : 'unfilterable-float';

export class ScriptComputeRuntime {
  private readonly _modules = new Map<string, ModuleRuntime>();
  private readonly _kernels = new Map<number, KernelInstance>();
  private readonly _buffers = new Map<number, GPUBuffer>();
  private readonly _textures = new Map<number, { texture: GPUTexture; view: GPUTextureView }>();
  private readonly _dummyBuffers = new Map<string, GPUBuffer>();
  private readonly _dummyTextures = new Map<string, GPUTextureView>();
  private _nextId = 1;
  private _lostDevice: GPUDevice | null = null;
  private _watchedDevice: GPUDevice | null = null;

  constructor(
    private readonly _getDevice: () => GPUDevice | null,
    private readonly _log: LogLike = console,
  ) {}

  /** True when a compute-capable device is up and not lost. */
  isAvailable(): boolean {
    return this.device() !== null;
  }

  private device(): GPUDevice | null {
    const device = this._getDevice();
    if (!device || device === this._lostDevice) return null;
    if (device !== this._watchedDevice) {
      this._watchedDevice = device;
      void device.lost.then((info) => {
        this._lostDevice = device;
        this._log.warn(`[gpu] device lost (${info.reason}) — script compute disabled`);
      });
    }
    return device;
  }

  // ---- Kernels ---------------------------------------------------------------------------

  /**
   * Instantiate a kernel from a compiled module artifact. `moduleKey` identifies the module for
   * sharing (content-addressed shader id / asset guid). Never rejects — resolves a dead handle
   * (`ok:false`, `error`) on any failure.
   */
  async createKernel(
    moduleKey: string,
    artifact: ComputeModuleArtifact,
    kernelName?: string,
  ): Promise<ComputeKernelRuntimeHandle> {
    const dead = (name: string, error: string): ComputeKernelRuntimeHandle => ({
      id: -1, ok: false, name, numThreads: [1, 1, 1], resources: [], error,
    });
    const device = this.device();
    if (!device) return dead(kernelName ?? moduleKey, 'no GPU device');

    const kernels = artifact.reflection.kernels;
    const kernel = kernelName
      ? kernels.find((k) => k.name === kernelName)
      : kernels.length === 1
        ? kernels[0]
        : undefined;
    if (!kernel) {
      const have = kernels.map((k) => k.name).join(', ') || 'none';
      return dead(
        kernelName ?? moduleKey,
        kernelName
          ? `kernel '${kernelName}' not found (module has: ${have})`
          : `module declares ${kernels.length} kernels (${have}) — pass a kernel name to loadKernel`,
      );
    }

    try {
      const mod = this.moduleRuntime(device, moduleKey, artifact);
      let pipeline = mod.pipelines.get(kernel.entryPoint);
      if (!pipeline) {
        pipeline = await device.createComputePipelineAsync({
          label: `script-compute:${kernel.name}`,
          layout: mod.pipelineLayout,
          compute: { module: mod.shaderModule, entryPoint: kernel.entryPoint },
        });
        mod.pipelines.set(kernel.entryPoint, pipeline);
      }

      const block = artifact.reflection.uniformBlock;
      const instance: KernelInstance = {
        module: mod,
        kernel,
        pipeline,
        uniformsDirty: block !== undefined,
        bound: new Map(),
        bindGroup: null,
        bindVersion: 1,
        builtVersion: 0,
        warnedMissing: false,
        label: kernel.name,
      };
      if (block) {
        instance.uniformData = new ArrayBuffer(block.stride);
        instance.uniformBuffer = device.createBuffer({
          label: `script-compute:${kernel.name}:uniforms`,
          size: block.stride,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        });
      }
      const id = this._nextId++;
      this._kernels.set(id, instance);
      return { id, ok: true, name: kernel.name, numThreads: kernel.numThreads, resources: kernel.bindings };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this._log.error(`[gpu] pipeline creation failed for '${kernel.name}': ${message}`);
      return dead(kernel.name, message);
    }
  }

  destroyKernel(kernel: number): void {
    const inst = this._kernels.get(kernel);
    if (!inst) return;
    inst.uniformBuffer?.destroy();
    this._kernels.delete(kernel);
  }

  /** Module runtime (shader module + explicit module-wide bind group layout), cached by key. */
  private moduleRuntime(device: GPUDevice, key: string, artifact: ComputeModuleArtifact): ModuleRuntime {
    const existing = this._modules.get(key);
    if (existing) return existing;

    const shaderModule = device.createShaderModule({ label: `script-compute:${key}`, code: artifact.wgsl });
    const entries: GPUBindGroupLayoutEntry[] = [];
    for (const b of artifact.reflection.bindings) {
      if (b.kind === 'uniform') continue; // lives in the shared uniform block below
      if (b.kind === 'buffer' || b.kind === 'rwbuffer') {
        entries.push({
          binding: b.binding,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: b.kind === 'buffer' ? 'read-only-storage' : 'storage' },
        });
      } else if (b.kind === 'texture') {
        entries.push({
          binding: b.binding,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: sampleTypeOf(b.type), viewDimension: '2d' },
        });
      } else {
        entries.push({
          binding: b.binding,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: {
            access: b.access === 'write' ? 'write-only' : 'read-write',
            format: (b.storageFormat ?? 'rgba32float') as GPUTextureFormat,
            viewDimension: '2d',
          },
        });
      }
    }
    const block = artifact.reflection.uniformBlock;
    if (block) {
      entries.push({ binding: block.binding, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } });
    }
    const layout = device.createBindGroupLayout({ label: `script-compute:${key}:layout`, entries });
    const mod: ModuleRuntime = {
      shaderModule,
      layout,
      pipelineLayout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      reflection: artifact.reflection,
      pipelines: new Map(),
    };
    this._modules.set(key, mod);
    return mod;
  }

  // ---- Buffers / textures ------------------------------------------------------------------

  createBuffer(byteLength: number, label?: string): number {
    const device = this.device();
    if (!device || byteLength <= 0) return -1;
    try {
      const buffer = device.createBuffer({
        label: label ? `script:${label}` : 'script:buffer',
        size: Math.ceil(byteLength / 4) * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
      });
      const id = this._nextId++;
      this._buffers.set(id, buffer);
      return id;
    } catch (err) {
      this._log.error('[gpu] createBuffer failed:', err);
      return -1;
    }
  }

  writeBuffer(buffer: number, data: ArrayBufferView, byteOffset = 0): void {
    const device = this.device();
    const target = this._buffers.get(buffer);
    if (!device || !target) return;
    device.queue.writeBuffer(target, byteOffset, data.buffer as ArrayBuffer, data.byteOffset, data.byteLength);
  }

  async readBuffer(buffer: number, byteLength?: number, byteOffset = 0): Promise<ArrayBuffer | null> {
    const device = this.device();
    const source = this._buffers.get(buffer);
    if (!device || !source) return null;
    const size = Math.min(byteLength ?? source.size - byteOffset, source.size - byteOffset);
    if (size <= 0) return null;
    try {
      const staging = device.createBuffer({
        label: 'script:readback',
        size: Math.ceil(size / 4) * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      const encoder = device.createCommandEncoder();
      encoder.copyBufferToBuffer(source, byteOffset, staging, 0, staging.size);
      device.queue.submit([encoder.finish()]);
      await staging.mapAsync(GPUMapMode.READ);
      const copy = staging.getMappedRange().slice(0, size);
      staging.unmap();
      staging.destroy();
      return copy;
    } catch (err) {
      this._log.error('[gpu] readBuffer failed:', err);
      return null;
    }
  }

  destroyBuffer(buffer: number): void {
    this._buffers.get(buffer)?.destroy();
    this._buffers.delete(buffer);
  }

  createTexture(width: number, height: number, format = 'rgba32float', label?: string): number {
    const device = this.device();
    if (!device || width <= 0 || height <= 0) return -1;
    try {
      const texture = device.createTexture({
        label: label ? `script:${label}` : 'script:texture',
        size: { width, height },
        format: format as GPUTextureFormat,
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.STORAGE_BINDING |
          GPUTextureUsage.COPY_DST |
          GPUTextureUsage.COPY_SRC,
      });
      const id = this._nextId++;
      this._textures.set(id, { texture, view: texture.createView() });
      return id;
    } catch (err) {
      this._log.error('[gpu] createTexture failed:', err);
      return -1;
    }
  }

  destroyTexture(texture: number): void {
    this._textures.get(texture)?.texture.destroy();
    this._textures.delete(texture);
  }

  // ---- Binding + uniforms --------------------------------------------------------------------

  /** Look up a kernel-addressable binding entry (own fields shadow module-level). */
  private entryFor(inst: KernelInstance, param: string): ComputeBindingEntry | undefined {
    return inst.kernel.bindings.find((b) => b.name === param);
  }

  setBuffer(kernel: number, param: string, buffer: number): void {
    const inst = this._kernels.get(kernel);
    if (!inst) return;
    const entry = this.entryFor(inst, param);
    if (!entry || (entry.kind !== 'buffer' && entry.kind !== 'rwbuffer')) {
      this._log.warn(`[gpu] ${inst.label}: no buffer parameter '${param}'`);
      return;
    }
    inst.bound.set(param, buffer);
    inst.bindVersion++;
  }

  setTexture(kernel: number, param: string, texture: number): void {
    const inst = this._kernels.get(kernel);
    if (!inst) return;
    const entry = this.entryFor(inst, param);
    if (!entry || (entry.kind !== 'texture' && entry.kind !== 'rwtexture')) {
      this._log.warn(`[gpu] ${inst.label}: no texture parameter '${param}'`);
      return;
    }
    inst.bound.set(param, texture);
    inst.bindVersion++;
  }

  setUniform(kernel: number, param: string, value: number | ArrayLike<number>): void {
    const inst = this._kernels.get(kernel);
    if (!inst?.uniformData) return;
    const entry = this.entryFor(inst, param);
    if (!entry || entry.kind !== 'uniform' || entry.offset === undefined) {
      this._log.warn(`[gpu] ${inst.label}: no uniform '${param}'`);
      return;
    }
    const values = typeof value === 'number' ? [value] : Array.from(value);
    const count = Math.min(values.length, Math.floor(entry.size / 4));
    const scalar = entry.type.startsWith('uint') ? 'u32' : entry.type.startsWith('int') ? 'i32' : 'f32';
    const view = new DataView(inst.uniformData, entry.offset, entry.size);
    for (let i = 0; i < count; i++) {
      if (scalar === 'f32') view.setFloat32(i * 4, values[i], true);
      else if (scalar === 'i32') view.setInt32(i * 4, values[i] | 0, true);
      else view.setUint32(i * 4, values[i] >>> 0, true);
    }
    inst.uniformsDirty = true;
  }

  // ---- Dispatch --------------------------------------------------------------------------------

  dispatch(kernel: number, x: number, y = 1, z = 1): boolean {
    const device = this.device();
    const inst = this._kernels.get(kernel);
    if (!device || !inst) return false;

    const bindGroup = this.bindGroupFor(device, inst);
    if (!bindGroup) return false;

    if (inst.uniformsDirty && inst.uniformBuffer && inst.uniformData) {
      device.queue.writeBuffer(inst.uniformBuffer, 0, inst.uniformData);
      inst.uniformsDirty = false;
    }
    const encoder = device.createCommandEncoder({ label: `script-compute:${inst.label}` });
    const pass = encoder.beginComputePass();
    pass.setPipeline(inst.pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(x, y, z);
    pass.end();
    device.queue.submit([encoder.finish()]);
    return true;
  }

  /** Build (or reuse) the instance's bind group over the MODULE-wide layout. */
  private bindGroupFor(device: GPUDevice, inst: KernelInstance): GPUBindGroup | null {
    if (inst.bindGroup && inst.builtVersion === inst.bindVersion) return inst.bindGroup;

    // The kernel's OWN addressable resources are required (we cannot see per-entry usage, so
    // strictness beats silently-dummy-bound wrong results).
    const missing = inst.kernel.bindings.filter(
      (b) => b.kind !== 'uniform' && !inst.bound.has(b.name),
    );
    if (missing.length) {
      if (!inst.warnedMissing) {
        inst.warnedMissing = true;
        this._log.warn(
          `[gpu] ${inst.label}: dispatch skipped — unbound: ${missing.map((b) => `${b.name} (${b.kind})`).join(', ')}`,
        );
      }
      return null;
    }
    inst.warnedMissing = false;

    try {
      const entries: GPUBindGroupEntry[] = [];
      const addressable = new Map(inst.kernel.bindings.map((b) => [b.name, b] as const));
      for (const b of inst.module.reflection.bindings) {
        if (b.kind === 'uniform') continue;
        const boundId = addressable.has(b.name) && addressable.get(b.name)!.owner === b.owner
          ? inst.bound.get(b.name)
          : undefined;
        if (b.kind === 'buffer' || b.kind === 'rwbuffer') {
          const gpuBuffer = boundId !== undefined ? this._buffers.get(boundId) : undefined;
          entries.push({ binding: b.binding, resource: { buffer: gpuBuffer ?? this.dummyBuffer(device, b) } });
        } else {
          const view = boundId !== undefined ? this._textures.get(boundId)?.view : undefined;
          entries.push({ binding: b.binding, resource: view ?? this.dummyTexture(device, b) });
        }
      }
      const block = inst.module.reflection.uniformBlock;
      if (block && inst.uniformBuffer) {
        entries.push({ binding: block.binding, resource: { buffer: inst.uniformBuffer } });
      }
      inst.bindGroup = device.createBindGroup({
        label: `script-compute:${inst.label}:bind`,
        layout: inst.module.layout,
        entries,
      });
      inst.builtVersion = inst.bindVersion;
      return inst.bindGroup;
    } catch (err) {
      this._log.error(`[gpu] ${inst.label}: bind group creation failed:`, err);
      return null;
    }
  }

  /** Dummy filler for sibling-kernel resources the current kernel doesn't address. */
  private dummyBuffer(device: GPUDevice, entry: ComputeBindingEntry): GPUBuffer {
    const size = Math.max(4, entry.size); // ≥ element stride so minBindingSize validation passes
    const key = `${entry.kind}:${size}`;
    let dummy = this._dummyBuffers.get(key);
    if (!dummy) {
      dummy = device.createBuffer({ label: `script:dummy:${key}`, size, usage: GPUBufferUsage.STORAGE });
      this._dummyBuffers.set(key, dummy);
    }
    return dummy;
  }

  private dummyTexture(device: GPUDevice, entry: ComputeBindingEntry): GPUTextureView {
    const format = entry.kind === 'rwtexture' ? (entry.storageFormat ?? 'rgba32float') : 'rgba32float';
    const key = `${entry.kind}:${format}`;
    let view = this._dummyTextures.get(key);
    if (!view) {
      const texture = device.createTexture({
        label: `script:dummy:${key}`,
        size: { width: 1, height: 1 },
        format: format as GPUTextureFormat,
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING,
      });
      view = texture.createView();
      this._dummyTextures.set(key, view);
    }
    return view;
  }

  // ---- Teardown --------------------------------------------------------------------------------

  /** Destroy everything (play-session end). The session backstop behind per-script scopes. */
  disposeAll(): void {
    for (const inst of this._kernels.values()) inst.uniformBuffer?.destroy();
    this._kernels.clear();
    for (const buffer of this._buffers.values()) buffer.destroy();
    this._buffers.clear();
    for (const t of this._textures.values()) t.texture.destroy();
    this._textures.clear();
    for (const dummy of this._dummyBuffers.values()) dummy.destroy();
    this._dummyBuffers.clear();
    this._dummyTextures.clear();
    this._modules.clear();
  }
}
